(() => {
  if (window.EchoesKeychainPreview) return;
  const images = new Map();
  const fonts = new Map();
  const loadImage = url => {
    if (!images.has(url)) {
      images.set(url, new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = () => { images.delete(url); reject(new Error('Preview image could not load')); };
        image.src = url;
      }));
    }
    return images.get(url);
  };
  const loadFont = url => {
    if (!fonts.has(url)) {
      fonts.set(url, new FontFace('EchoesKeychainScript', `url("${url}")`).load()
        .then(font => document.fonts.add(font)).catch(() => null));
    }
    return fonts.get(url);
  };
  const makeCanvas = (width, height) => Object.assign(document.createElement('canvas'), { width, height });

  class EchoesKeychainPreview {
    constructor(owner) {
      this.owner = owner;
      this.scope = owner.closest('.pro_details_pos');
      this.gallery = this.scope?.querySelector('.product_detail_img');
      const data = owner.querySelector('[data-keychain-preview-config]');
      if (!this.gallery || !data) return;
      this.config = JSON.parse(data.textContent);
      this.controller = new AbortController();
      this.sequence = 0;
      this.photoSequence = 0;
      this.element = document.createElement('div');
      this.element.className = 'keychain-live-preview';
      this.element.hidden = true;
      this.element.innerHTML = `
        <button type="button" class="keychain-preview__open" aria-label="Enlarge your keychain preview">
          <canvas width="960" height="960" data-main-preview role="img" aria-label="Keychain design preview"></canvas>
          <span class="keychain-preview__zoom-hint">View larger</span>
        </button>
        <p class="keychain-preview__selection" data-preview-selection aria-live="polite"></p>
        <div class="keychain-preview__shapes" aria-label="Preview and select a keychain shape"></div>
        <p class="keychain-preview__note" data-preview-note></p>
        <p class="keychain-preview__status" data-preview-status role="status" hidden></p>
        <dialog class="keychain-preview__dialog" aria-label="Your keychain preview">
          <button type="button" class="keychain-preview__close" aria-label="Close enlarged preview">&#215;</button>
          <canvas width="960" height="960" data-large-preview role="img" aria-label="Enlarged keychain design preview"></canvas>
          <p data-large-preview-caption></p>
        </dialog>`;
      this.gallery.prepend(this.element);
      this.canvas = this.element.querySelector('[data-main-preview]');
      this.dialog = this.element.querySelector('dialog');
      this.largeCanvas = this.element.querySelector('[data-large-preview]');
      this.thumbnails = new Map();
      const { signal } = this.controller;
      for (const shape of ['Rectangle', 'Round', 'Heart']) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'keychain-preview__shape';
        button.dataset.shape = shape;
        button.setAttribute('aria-label', `Select ${shape.toLowerCase()} keychain`);
        button.setAttribute('aria-pressed', 'false');
        const canvas = makeCanvas(180, 180);
        canvas.setAttribute('aria-hidden', 'true');
        button.append(canvas, document.createTextNode(shape));
        button.addEventListener('click', () => {
          const option = this.scope.querySelector(`pr-option input[value="${shape}"]`);
          if (option) option.click();
          this.schedule();
        }, { signal });
        this.element.querySelector('.keychain-preview__shapes').append(button);
        this.thumbnails.set(shape, { button, canvas });
      }
      this.element.querySelector('.keychain-preview__open').addEventListener('click', () => this.dialog.showModal(), { signal });
      this.owner.querySelector('[data-open-keychain-preview]')?.addEventListener('click', () => this.dialog.showModal(), { signal });
      this.element.querySelector('.keychain-preview__close').addEventListener('click', () => this.dialog.close(), { signal });
      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); }, { signal });
      this.scope.addEventListener('change', event => {
        if (event.target.matches('pr-option input, selects-box select, input[name="id"]')) this.schedule();
      }, { signal });
      this.schedule();
    }

    schedule() {
      if (!this.element) return;
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.render());
    }

    setPhoto(file) {
      const request = ++this.photoSequence;
      if (this.photoUrl) URL.revokeObjectURL(this.photoUrl);
      this.photoUrl = null;
      this.photo = null;
      if (!file || !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 10 * 1024 * 1024) {
        this.schedule();
        return;
      }
      const url = URL.createObjectURL(file);
      this.photoUrl = url;
      const image = new Image();
      image.onload = () => {
        if (request !== this.photoSequence) return;
        this.photo = image;
        this.schedule();
      };
      image.onerror = () => {
        if (request !== this.photoSequence) return;
        this.schedule();
        this.setStatus('This photo could not be previewed. Try a different JPG, PNG or WEBP image.');
      };
      image.src = url;
    }

    setStatus(message) {
      const status = this.element.querySelector('[data-preview-status]');
      status.textContent = message;
      status.hidden = !message;
    }

    draw(shape, background, loaded, photo, name) {
      const spec = this.config.render.shapes[shape];
      const artwork = makeCanvas(spec.artWidth, spec.artHeight);
      const art = artwork.getContext('2d');
      art.fillStyle = '#ffffff';
      art.fillRect(0, 0, artwork.width, artwork.height);
      const bg = spec.background;
      art.drawImage(background, bg.left, bg.top, bg.width, bg.height);
      if (photo) {
        // Place the supplied photo without claiming to apply an AI portrait effect.
        const box = { x: artwork.width * 0.11, y: artwork.height * 0.13, width: artwork.width * 0.78, height: artwork.height * 0.64 };
        const scale = Math.min(box.width / photo.naturalWidth, box.height / photo.naturalHeight);
        const width = photo.naturalWidth * scale;
        const height = photo.naturalHeight * scale;
        art.save();
        art.beginPath();
        art.roundRect(box.x + (box.width - width) / 2, box.y + (box.height - height) / 2, width, height, Math.min(width, height) * 0.06);
        art.clip();
        art.drawImage(photo, box.x + (box.width - width) / 2, box.y + (box.height - height) / 2, width, height);
        art.restore();
      } else {
        const portrait = spec.portrait;
        art.drawImage(loaded.portrait, portrait.left, portrait.top, portrait.width, portrait.height);
      }
      if (name) {
        const text = spec.name;
        let size = text.fontSize;
        const maxWidth = photo ? artwork.width * 0.6 : text.maxWidth * 1.35;
        art.font = `${size}px "EchoesKeychainScript", cursive`;
        while (art.measureText(name).width > maxWidth && size > 12) {
          size -= 1;
          art.font = `${size}px "EchoesKeychainScript", cursive`;
        }
        art.fillStyle = text.color;
        art.textAlign = 'center';
        art.textBaseline = 'top';
        art.fillText(name, photo ? artwork.width / 2 : text.x, photo ? artwork.height * 0.8 : text.y, maxWidth);
      }
      const canvas = makeCanvas(960, 960);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(loaded.base, 0, 0, 960, 960);
      const area = spec.printArea;
      ctx.drawImage(artwork, area.left, area.top, area.width, area.height);
      ctx.drawImage(loaded.overlay, 0, 0, 960, 960);
      return canvas;
    }

    async render() {
      const version = ++this.sequence;
      const backgroundOption = this.owner.querySelector('[data-background-option]:checked');
      if (!backgroundOption) return;
      const form = document.getElementById(this.owner.dataset.formId);
      const variantId = form?.querySelector('input[name="id"]')?.value || this.config.initialVariantId;
      const variant = this.config.variants.find(item => String(item.id) === String(variantId));
      const shape = variant?.option1 || 'Rectangle';
      if (!this.config.render.shapes[shape]) return;
      const name = this.owner.querySelector('[data-pet-name]').value.trim();
      const photo = this.photo;
      this.element.setAttribute('aria-busy', 'true');
      try {
        const shapeNames = Object.keys(this.config.render.shapes);
        const [background, layers] = await Promise.all([
          loadImage(backgroundOption.dataset.artwork),
          Promise.all(shapeNames.map(async key => {
            const spec = this.config.render.shapes[key];
            const [base, overlay, portrait] = await Promise.all([loadImage(spec.base), loadImage(spec.overlay), loadImage(spec.portrait.url)]);
            return { shape: key, base, overlay, portrait };
          })),
          loadFont(this.config.render.font)
        ]);
        if (version !== this.sequence || !this.owner.isConnected) return;
        const renders = new Map(layers.map(layer => [layer.shape, this.draw(layer.shape, background, layer, photo, name)]));
        this.canvas.getContext('2d').drawImage(renders.get(shape), 0, 0);
        this.largeCanvas.getContext('2d').drawImage(renders.get(shape), 0, 0);
        for (const [key, thumbnail] of this.thumbnails) {
          thumbnail.canvas.getContext('2d').drawImage(renders.get(key), 0, 0, 180, 180);
          thumbnail.button.setAttribute('aria-pressed', String(key === shape));
        }
        const caption = `${shape} · ${backgroundOption.value}${name ? ` · ${name}` : ''}`;
        this.canvas.setAttribute('aria-label', `Keychain preview: ${caption}`);
        this.largeCanvas.setAttribute('aria-label', `Enlarged keychain preview: ${caption}`);
        this.element.querySelector('[data-preview-selection]').textContent = caption;
        this.element.querySelector('[data-large-preview-caption]').textContent = caption;
        this.element.querySelector('[data-preview-note]').textContent = photo
          ? 'Photo placement preview. Your final watercolor portrait is prepared from your photo.'
          : 'Background preview with a sample pet. Upload your photo to preview its placement.';
        this.element.dataset.background = backgroundOption.value;
        this.element.dataset.shape = shape;
        this.element.dataset.previewMode = photo ? 'photo' : 'sample';
        this.element.dataset.name = name;
        this.element.dataset.renderVersion = String(version);
        const openButton = this.owner.querySelector('[data-open-keychain-preview]');
        if (openButton) openButton.disabled = false;
        this.element.hidden = false;
        this.element.classList.remove('keychain-preview--error');
        this.gallery.classList.add('has-keychain-live-preview');
        this.setStatus('');
      } catch (error) {
        if (version !== this.sequence) return;
        this.element.hidden = false;
        this.element.classList.add('keychain-preview--error');
        this.setStatus('The preview could not load. Your selected background will still be included with your order.');
      } finally {
        if (version === this.sequence) this.element.setAttribute('aria-busy', 'false');
      }
    }

    destroy() {
      ++this.sequence;
      ++this.photoSequence;
      cancelAnimationFrame(this.frame);
      this.controller?.abort();
      if (this.photoUrl) URL.revokeObjectURL(this.photoUrl);
      this.element?.remove();
      this.gallery?.classList.remove('has-keychain-live-preview');
    }
  }
  window.EchoesKeychainPreview = EchoesKeychainPreview;
})();
